import React, {
  PropsWithChildren,
  ReactNode,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { TbInfoCircle } from 'react-icons/tb';
import { wrap, trigger, bubble } from './style';

interface Props {
  content: ReactNode;
  label?: string;
  placement?: 'bottom-start' | 'bottom-end';
}

function Tooltip({
  content,
  label = '안내',
  placement = 'bottom-start',
  children,
}: PropsWithChildren<Props>) {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [shift, setShift] = useState(0);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !bubbleRef.current) {
      setShift(0);
      return;
    }
    const { left, right } = bubbleRef.current.getBoundingClientRect();
    const GUTTER = 16;
    if (right > window.innerWidth - GUTTER) {
      setShift(window.innerWidth - GUTTER - right);
    } else if (left < GUTTER) {
      setShift(GUTTER - left);
    }
  }, [isOpen]);

  return (
    <span
      css={wrap}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        css={trigger}
        aria-label={label}
        aria-describedby={isOpen ? id : undefined}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {children ?? <TbInfoCircle size={18} />}
      </button>
      {isOpen && (
        <span
          ref={bubbleRef}
          role="tooltip"
          id={id}
          css={bubble(placement)}
          style={{ transform: `translateX(${shift}px)` }}
        >
          {content}
        </span>
      )}
    </span>
  );
}

export default Tooltip;
